import React from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Box } from '@mui/material'
import GenericModal from '../../../../components/GenericModal'
import FormSwitch from '../../../../components/FormElements/FormSwitch'
import { type Row } from '../../../../components/CustomTable/types'
import { type Instructor, type OptedOutInstructor } from '../../../../models/TermsSection'

export interface InstructorOptOutEntry {
  instructorUserId: string
  isOptedOut: boolean
}

interface TeachingUser {
  id: string
  title: string
  role: string
}

interface InstructorOptOutModalProps {
  row: Row
  onClose: () => void
  onConfirm: (instructors: InstructorOptOutEntry[]) => void
  isSubmitting?: boolean
}

// Roster = Instructor + Additional Instructor (row.data.instructors) + TA (row.data.teachingAssistants).
// Staff is intentionally excluded: the CDG row payload has no confirmed Staff field to source it
// from (see docs/specs/active/instructor-level-opt-out/spec.md Open Questions) — flagged on the PR
// rather than guessing a field name.
const getTeachingUsers = (row: Row): TeachingUser[] => {
  const instructors: Instructor[] = Array.isArray(row?.data?.instructors) ? row.data.instructors : []
  const teachingAssistants: Instructor[] = Array.isArray(row?.data?.teachingAssistants) ? row.data.teachingAssistants : []

  return [
    ...instructors.map((instructor) => ({ id: instructor.id, title: instructor.title, role: instructor.roleName ?? 'Instructor' })),
    ...teachingAssistants.map((ta) => ({ id: ta.id, title: ta.title, role: ta.roleName ?? 'TA' }))
  ]
}

const InstructorOptOutModal: React.FC<InstructorOptOutModalProps> = ({ row, onClose, onConfirm, isSubmitting = false }) => {
  const teachingUsers = getTeachingUsers(row)
  const optedOutInstructors: OptedOutInstructor[] = Array.isArray(row?.data?.optedOutInstructors) ? row.data.optedOutInstructors : []
  const optedOutUserIds = new Set(optedOutInstructors.map((instructor) => instructor.userId))

  const form = useForm<Record<string, boolean>>({
    defaultValues: Object.fromEntries(teachingUsers.map((user) => [user.id, optedOutUserIds.has(user.id)]))
  })

  const handleConfirm = async (values: Record<string, boolean>): Promise<void> => {
    onConfirm(teachingUsers.map((user) => ({
      instructorUserId: user.id,
      isOptedOut: Boolean(values[user.id])
    })))
  }

  const abbreviation = row?.data?.linkedSection?.abbreviation ?? ''

  return (
    <GenericModal
      isOpen
      onClose={onClose}
      title={`Opt out instructors — ${abbreviation}`}
      maxWidth='sm'
      confirmButtonLoading={isSubmitting}
      onConfirm={form.handleSubmit(handleConfirm)}
    >
      <FormProvider {...form}>
        <Box display='flex' flexDirection='column' gap={2} alignItems='flex-start'>
          {teachingUsers.map((user) => (
            <FormSwitch
              key={user.id}
              name={user.id}
              label={`${user.title} (${user.role})`}
              // Constrain each row to its own content width so the switch always lines up on the
              // left, instead of stretching full-width and centering (which shifted the toggle
              // left/right per row depending on how long that instructor's name/role text was).
              sx={{ width: 'fit-content' }}
              supportText={(isOptedOut) => `${isOptedOut ? 'Opted out' : 'Not opted out'} for ${abbreviation}`}
            />
          ))}
        </Box>
      </FormProvider>
    </GenericModal>
  )
}

export default InstructorOptOutModal
